// Descendancy view: pick an ancestor, see everyone who came from them.
//
// Every other layout here is ego-centred - the chart, the fan and the timeline
// all answer questions about YOU, and the chart in particular draws each person
// exactly once, hanging off whichever parents claimed them first. That makes
// one very ordinary genealogy question unanswerable: "who are all of my
// great-grandmother's descendants?" The answer is spread across the chart in
// branches you have to trace by eye, and the collateral lines - your gran's
// sister, her children, their children, i.e. your cousins - are exactly the
// part that is hardest to follow.
//
// So this view inverts the question. One person is the root, and everything
// below is their descent, drawn as an indented outline rather than a chart:
// depth reads as indentation, which stays legible at eight generations where a
// chart is already metres wide. Spouses ride ON their partner's row rather than
// taking one of their own - they are how the line continues, not part of the
// descent - which is the same convention printed descendancy reports use.
//
// The root is a real piece of state, not a view-local toggle like the table's
// sort: it is in the URL (?view=descendancy&root=<id>), so a descendancy is a
// thing you can link someone to. contact_tree_controller owns that plumbing and
// hands us rootId; we only report a change back through ctx.onRootChange.
import { birthOf, lifespanLabel, personOpenable, applyPersonLink } from "./genealogy"

// A guard, not a design limit. The edge set is user-entered and a cycle
// (A parent of B, B parent of A) is only prevented per-pair by the model, so a
// walk down `children` must terminate on its own rather than trusting the data.
const MAX_DEPTH = 24

export function renderDescendancy(ctx) {
    let wrap = document.createElement("div")
    wrap.className = "ft-desc"

    let roots = candidateRoots(ctx)
    if (!roots.length) {
        wrap.appendChild(emptyState("Link a parent to a child and the line they start will show up here."))
        return { element: wrap, panZoom: false }
    }

    // Resolve to the node OBJECT, never to an id: node ids arrive from JSON as
    // numbers but ctx.rootId comes off the URL as a string, so a Map lookup
    // with the raw value silently misses and leaves the view blank.
    let root = roots.find((node) => String(node.id) === String(ctx.rootId)) || defaultRoot(roots, ctx)

    // "Whose line?" is a filter, so the picker goes on the shared toolbar row
    // next to the search and the layout switcher; it only rides above the
    // outline where no slot was provided.
    let bar = rootBar(roots, root, ctx)
    if (ctx.filterSlot) ctx.filterSlot.appendChild(bar)
    else wrap.appendChild(bar)

    let seen = new Set()
    let list = document.createElement("ol")
    list.className = "ft-desc-list"
    list.appendChild(personRow(root, ctx, 0, seen))
    wrap.appendChild(list)

    let total = seen.size - 1
    let foot = document.createElement("p")
    foot.className = "ft-desc-foot"
    foot.textContent = total > 0
        ? `${total} ${total === 1 ? "descendant" : "descendants"} of ${root.name}, across ${generationCount(root, ctx)} generations.`
        : `No children recorded for ${root.name} yet.`
    wrap.appendChild(foot)

    return { element: wrap, panZoom: false }
}

// Who can head a descendancy. Anyone with children qualifies - you may well
// want to root on your mother rather than only on the topmost ancestor - but
// people with no children at all would give an outline of one row, so they are
// left out of the picker rather than offered as a dead end.
function candidateRoots(ctx) {
    return ctx.nodes
        .filter((node) => (ctx.childrenOf.get(node.id) || []).some((id) => ctx.nodesById.has(id)))
        .sort((a, b) => {
            let ay = birthOf(a).year
            let by = birthOf(b).year
            if (Number.isFinite(ay) && Number.isFinite(by) && ay !== by) return ay - by
            if (Number.isFinite(ay) !== Number.isFinite(by)) return Number.isFinite(ay) ? -1 : 1
            return a.name.localeCompare(b.name)
        })
}

// Default to the top of the viewer's OWN line - walk up from ego as far as the
// recorded parents go - so the view opens on the deepest thing it can say about
// you, rather than on whoever happens to sort first.
function defaultRoot(roots, ctx) {
    let byId = new Map(roots.map((node) => [String(node.id), node]))
    let current = ctx.egoId
    let best = null
    for (let step = 0; step < MAX_DEPTH && current != null; step++) {
        if (byId.has(String(current))) best = byId.get(String(current))
        let parents = (ctx.parentsOf.get(current) || []).filter((id) => ctx.nodesById.has(id))
        if (!parents.length) break
        current = parents[0]
    }
    return best || roots[0]
}

function rootBar(roots, root, ctx) {
    let bar = document.createElement("div")
    bar.className = "ft-desc-bar"

    let label = document.createElement("label")
    label.className = "ft-desc-bar-label"
    label.textContent = "Descendants of"
    label.htmlFor = "ft-desc-root"
    bar.appendChild(label)

    let select = document.createElement("select")
    select.className = "form-select form-select-sm ft-desc-root"
    select.id = "ft-desc-root"
    roots.forEach((node) => {
        let option = document.createElement("option")
        option.value = String(node.id)
        let year = birthOf(node).year
        option.textContent = Number.isFinite(year) ? `${node.name} (b. ${year})` : node.name
        option.selected = String(node.id) === String(root.id)
        select.appendChild(option)
    })
    select.addEventListener("change", () => ctx.onRootChange(select.value))
    bar.appendChild(select)
    return bar
}

// One person, then their children nested beneath. `seen` is both the
// dedup set and the descendant count, and it is what stops a cycle: a person
// already drawn on this walk is marked and not descended into again.
function personRow(node, ctx, depth, seen) {
    let li = document.createElement("li")
    li.className = "ft-desc-item"

    let row = document.createElement("div")
    row.className = "ft-desc-row"
    row.dataset.nodeId = node.id

    let repeat = seen.has(node.id)
    seen.add(node.id)

    let name = document.createElement(personOpenable(node) ? "a" : "span")
    name.className = "ft-desc-name"
    if (personOpenable(node)) applyPersonLink(name, node)
    name.textContent = node.name
    row.appendChild(name)

    // A lifespan rather than "b. 1922" - a descendancy is mostly people the
    // family has lost, and a column of birth years says nothing about which.
    let span = lifespanLabel(node)
    if (span) {
        row.appendChild(meta(node.deceased ? span : `b. ${span}`, "ft-desc-year"))
    }

    // Spouses ride on the row rather than taking one of their own - see the
    // header. They are named, not linked onward: their own line belongs to
    // whoever their parents are, not to this descent.
    let partners = (ctx.partnersOf.get(node.id) || [])
        .map((id) => ctx.nodesById.get(id))
        .filter(Boolean)
    if (partners.length) {
        row.appendChild(meta(`& ${partners.map((p) => p.name).join(" & ")}`, "ft-desc-partner"))
    }

    if (node.relation) row.appendChild(meta(node.relation, "ft-desc-relation"))
    li.appendChild(row)

    let children = (ctx.childrenOf.get(node.id) || [])
        .map((id) => ctx.nodesById.get(id))
        .filter(Boolean)

    if (repeat || depth >= MAX_DEPTH || !children.length) {
        if (repeat && children.length) row.appendChild(meta("shown above", "ft-desc-repeat"))
        return li
    }

    // Oldest first, so a sibling row reads in birth order like a family bible
    // rather than in whatever order the edges were entered.
    children.sort((a, b) => {
        let ay = birthOf(a).year
        let by = birthOf(b).year
        if (Number.isFinite(ay) && Number.isFinite(by) && ay !== by) return ay - by
        if (Number.isFinite(ay) !== Number.isFinite(by)) return Number.isFinite(ay) ? -1 : 1
        return a.name.localeCompare(b.name)
    })

    let sublist = document.createElement("ol")
    sublist.className = "ft-desc-sublist"
    children.forEach((child) => sublist.appendChild(personRow(child, ctx, depth + 1, seen)))
    li.appendChild(sublist)
    return li
}

function generationCount(root, ctx) {
    let depth = 0
    let frontier = [root.id]
    let seen = new Set([root.id])
    while (frontier.length && depth < MAX_DEPTH) {
        let next = []
        frontier.forEach((id) => {
            (ctx.childrenOf.get(id) || []).forEach((childId) => {
                if (!ctx.nodesById.has(childId) || seen.has(childId)) return
                seen.add(childId)
                next.push(childId)
            })
        })
        if (!next.length) break
        depth += 1
        frontier = next
    }
    return depth + 1
}

function meta(text, className) {
    let span = document.createElement("span")
    span.className = className
    span.textContent = text
    return span
}

function emptyState(message) {
    let box = document.createElement("div")
    box.className = "ft-view-empty"
    let h = document.createElement("h2")
    h.className = "ft-view-empty-title"
    h.textContent = "No lines to follow yet"
    box.appendChild(h)
    let p = document.createElement("p")
    p.className = "text-muted small mb-0"
    p.textContent = message
    box.appendChild(p)
    return box
}
